import React, { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    TextField,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loadSystemConfigurations, selectSystemConfigurations } from '../store/systemConfigurations.reducer';
import ToastWrapper from './ToastWrapper';
import { useToastMessage } from '../hooks/useToastMessage';
import { getContainerVersions } from '../api/getContainerVersions';
import { getApplicationsVersions } from '../api/getApplicationsVersions';
import { ProcessedMapListItem, ToastType } from '../interfaces';
import { createSystemConfiguration } from '../api/createSystemConfiguration';
import { FormikAutocomplete } from './FormikAutocomplete';
import { getProcessedMaps } from '../api/getProcessedMaps';
import { stripEmptyFields } from '../utils/stripEmptyFields';

interface SystemConfigurationForm {
    systemVersion?: string;
    containerVersion?: string;
    applicationVersion?: string;
    mapVersion?: string;
    osVersion?: string;
    odometryOn?: boolean;
}

export const AddSystemConfigurationDialog = ({ open, onClose }: any) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    const [containerVersions, setContainerVersions] = useState<string[] | null>(null);
    const [applicationVersions, setApplicationVersions] = useState<string[] | null>(null);
    const [processedMaps, setProcessedMaps] = useState<ProcessedMapListItem[] | null>(null);
    const systemConfigurations = useAppSelector(selectSystemConfigurations);
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();

    const initialValues: SystemConfigurationForm = {
        systemVersion: '',
        containerVersion: '',
        applicationVersion: '',
        mapVersion: '',
        osVersion: '',
        odometryOn: false,
    };

    const validateForm = (values: any) => {
        const errors: SystemConfigurationForm = {};
        if (!values.systemVersion) {
            errors.systemVersion = 'Required';
        } else if (systemConfigurations?.find((c) => c.systemVersion === values.systemVersion)) {
            errors.systemVersion = 'Конфігурація з такою назвою вже існує';
        }

        if (!values.applicationVersion) {
            errors.applicationVersion = 'Required';
        }
        if (!values.mapVersion) {
            errors.mapVersion = 'Required';
        }
        return errors;
    };

    const handleSubmit = async (values: SystemConfigurationForm, { setSubmitting }: any) => {
        try {
            // containerVersion and osVersion are optional; blank ones are dropped
            // so they are not stored as empty strings. `odometryOn: false` is kept.
            await createSystemConfiguration(stripEmptyFields(values));
            dispatch(loadSystemConfigurations());
        } catch (error) {
            setToastMessage({
                message: `Під час створенні конфігурації відбулася помилка`,
                type: ToastType.WARNING,
            });
        } finally {
            onClose();
        }
        setSubmitting(false);
    };

    useEffect(() => {
        (async () => {
            setLoading(true);

            await Promise.all([
                getContainerVersions()
                    .then((containerVersions) => setContainerVersions(containerVersions.reverse()))
                    .catch((e) => {
                        console.error(e);
                        setToastMessage({
                            message: `При отриманні версій контейнера відбулася помилка`,
                            type: ToastType.WARNING,
                        });
                    }),
                getApplicationsVersions()
                    .then((applicationVersions) => setApplicationVersions(applicationVersions.reverse()))
                    .catch((e) => {
                        console.error(e);
                        setToastMessage({
                            message: `При отриманні версій апплікації відбулася помилка`,
                            type: ToastType.WARNING,
                        });
                    }),
                getProcessedMaps()
                    .then((processedMaps) => setProcessedMaps(processedMaps))
                    .catch((e) => {
                        console.error(e);
                        setToastMessage({
                            message: `При отриманні списку доступних карт відбулася помилка`,
                            type: ToastType.WARNING,
                        });
                    }),
            ]);

            setLoading(false);
        })();
    }, []);

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Add System Configuration</DialogTitle>
            <DialogContent sx={{ width: 350, minHeight: 307 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '110px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Formik initialValues={initialValues} validate={validateForm} onSubmit={handleSubmit}>
                        {({ errors, touched, isSubmitting }) => (
                            <Form>
                                <Box sx={{ marginTop: 1, marginBottom: 1 }}>
                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="systemVersion"
                                            label="System Version Name *"
                                            variant="outlined"
                                            size="small"
                                            error={
                                                touched.systemVersion && errors.systemVersion
                                                    ? true
                                                    : undefined
                                            }
                                            helperText={touched.systemVersion && errors.systemVersion}
                                        />
                                    </FormControl>

                                    {containerVersions && (
                                        <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                            <Field
                                                as={FormikAutocomplete}
                                                size="small"
                                                name="containerVersion"
                                                label="Container Version"
                                                options={containerVersions}
                                                error={
                                                    touched.containerVersion && errors.containerVersion
                                                        ? true
                                                        : undefined
                                                }
                                            />
                                        </FormControl>
                                    )}

                                    {applicationVersions && (
                                        <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                            <Field
                                                as={FormikAutocomplete}
                                                size="small"
                                                name="applicationVersion"
                                                label="Application Version *"
                                                options={applicationVersions}
                                                error={
                                                    touched.applicationVersion && errors.applicationVersion
                                                        ? true
                                                        : undefined
                                                }
                                            />
                                        </FormControl>
                                    )}

                                    {processedMaps && (
                                        <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                            <Field
                                                as={FormikAutocomplete}
                                                size="small"
                                                name="mapVersion"
                                                label="Map Version *"
                                                options={processedMaps.map((m) => m.name)}
                                                error={
                                                    touched.mapVersion && errors.mapVersion ? true : undefined
                                                }
                                            />
                                        </FormControl>
                                    )}

                                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                        <Field
                                            as={TextField}
                                            name="osVersion"
                                            label="OS Version"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </FormControl>

                                    <FormControlLabel
                                        control={
                                            <Field
                                                as={Checkbox}
                                                name="odometryOn"
                                                type="checkbox"
                                                size="small"
                                            />
                                        }
                                        label="Odometry On"
                                        sx={{ marginBottom: 1 }}
                                    />

                                    <DialogActions>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={onClose}
                                            color="error"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            size="small"
                                            variant="outlined"
                                            disabled={isSubmitting}
                                            color="primary"
                                        >
                                            Add {isSubmitting && <CircularProgress size={15} />}
                                        </Button>
                                    </DialogActions>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                )}
            </DialogContent>
            <ToastWrapper
                open={isOpened}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={3000}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </Dialog>
    );
};
